<template>
  <AppSidebar>
    <div class="space-y-10 pb-12">
      <!-- Page Header -->
      <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-8 pb-4">
        <div class="space-y-2">
            <div class="flex items-center gap-3 mb-1">
                <div class="w-2 h-2 bg-primary-500 rounded-full shadow-[0_0_12px_rgba(20,184,166,0.5)] animate-pulse"></div>
                <span class="text-[11px] font-bold text-primary-600 dark:text-primary-400 uppercase tracking-widest">Legal System</span>
            </div>
            <h1 class="text-4xl font-bold text-gray-900 dark:text-white sm:text-5xl uppercase tracking-tight" style="font-family: Poppins, sans-serif">
                Compliance <span class="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-blue-500">Registry</span>
            </h1>
            <p class="text-sm text-gray-500 dark:text-gray-400 font-medium max-w-md">
                Manage system-wide legal policies, privacy terms, and regulatory documentation.
            </p>
        </div>
        <div class="flex items-center gap-4">
            <button @click="openCreateModal"
                class="group relative inline-flex items-center px-8 py-4 bg-gray-900 dark:bg-primary-600 text-white text-sm font-semibold rounded-2xl hover:scale-105 transition-all shadow-2xl shadow-primary-500/20 active:scale-95 overflow-hidden">
                <div class="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                <PlusIcon class="w-4 h-4 mr-3" />
                Initialize Policy
            </button>
        </div>
      </div>

      <!-- Legal Pages Table Container -->
      <div class="bg-white dark:bg-gray-800/40 rounded-[40px] border border-gray-100 dark:border-gray-700/50 shadow-2xl shadow-gray-200/50 dark:shadow-none overflow-hidden backdrop-blur-xl">
        <!-- Loading State -->
        <div v-if="loading" class="flex flex-col items-center justify-center py-24 space-y-4">
          <div class="relative w-16 h-16">
            <div class="absolute inset-0 border-4 border-primary-500/20 rounded-full"></div>
            <div class="absolute inset-0 border-4 border-primary-500 rounded-full border-t-transparent animate-spin"></div>
          </div>
          <span class="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Loading Pages...</span>
        </div>

        <!-- Table Content -->
        <div v-else-if="legalPages.length > 0" class="overflow-x-auto custom-scrollbar">
          <table class="w-full">
            <thead>
              <tr class="bg-gray-50/50 dark:bg-gray-900/40 border-b border-gray-100 dark:border-gray-800/50">
                <th class="px-8 py-6 text-left text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Page Title</th>
                <th class="px-8 py-6 text-left text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">URL Slug</th>
                <th class="px-8 py-6 text-center text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Status</th>
                <th class="px-8 py-6 text-center text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Last Updated</th>
                <th class="px-8 py-6 text-right text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-50 dark:divide-gray-800/50">
              <tr v-for="page in legalPages" :key="page.id" class="group hover:bg-gray-50/50 dark:hover:bg-gray-800/20 transition-all duration-300">
                <td class="px-8 py-6">
                  <div class="flex items-center gap-4">
                    <div class="p-3 bg-gray-50 dark:bg-gray-900/40 rounded-xl group-hover:scale-110 transition-transform">
                      <DocumentTextIcon class="w-5 h-5 text-gray-400 dark:text-gray-500 group-hover:text-primary-500 transition-colors" />
                    </div>
                    <div>
                      <div class="text-[13px] font-bold text-gray-900 dark:text-white uppercase tracking-wider" style="font-family: Poppins, sans-serif">{{ page.title }}</div>
                      <div class="text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mt-0.5">Corporate Policy</div>
                    </div>
                  </div>
                </td>
                <td class="px-8 py-6">
                  <div class="inline-flex items-center px-3 py-1 bg-gray-50 dark:bg-gray-900/40 rounded-lg text-xs font-bold text-gray-500 dark:text-gray-400 font-mono tracking-wider border border-gray-100 dark:border-gray-800">
                    /{{ page.slug }}
                  </div>
                </td>
                <td class="px-8 py-6">
                  <div class="flex justify-center">
                    <button @click="toggleStatus(page)"
                      class="px-5 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest border shadow-sm transition-all active:scale-95"
                      :class="page.is_active 
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800/50' 
                        : 'bg-gray-50 text-gray-700 border-gray-100 dark:bg-gray-900/40 dark:text-gray-500 dark:border-gray-800'">
                      {{ page.is_active ? 'Active' : 'Draft' }}
                    </button>
                  </div>
                </td>
                <td class="px-8 py-6 text-center text-[11px] font-black text-gray-600 dark:text-gray-400 uppercase tracking-widest">
                  {{ formatDate(page.updated_at) }}
                </td>
                <td class="px-8 py-6 text-right">
                  <div class="flex items-center justify-end gap-3 opacity-0 group-hover:opacity-100 transition-all duration-300">
                    <button @click="openEditModal(page)"
                      class="p-2.5 text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-xl transition-all"
                      title="Adjust Metrics">
                      <PencilSquareIcon class="w-5 h-5" />
                    </button>
                    <button @click="confirmDelete(page)"
                      class="p-2.5 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-xl transition-all"
                      title="Purge Protocol">
                      <TrashIcon class="w-5 h-5" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Empty State -->
        <div v-else class="flex flex-col items-center justify-center py-24 px-8 text-center space-y-6">
          <div class="p-8 bg-gray-50 dark:bg-gray-900/20 rounded-[40px]">
            <ScaleIcon class="mx-auto h-20 w-20 text-gray-200 dark:text-gray-800" />
          </div>
          <div class="space-y-2">
            <h3 class="text-xl font-bold text-gray-900 dark:text-white uppercase tracking-tight">No Legal Pages Found</h3>
            <p class="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest max-w-[280px]">Start by creating your first legal document.</p>
          </div>
          <button @click="openCreateModal"
              class="inline-flex items-center px-10 py-4 bg-primary-500 text-white text-xs font-bold uppercase tracking-[0.2em] rounded-2xl hover:scale-105 transition-all shadow-xl shadow-primary-500/20 active:scale-95">
              Add Legal Provision
          </button>
        </div>
      </div>
    </div>

    <!-- Edit/Create Modal -->
    <div v-if="showModal" class="fixed inset-0 z-[60] overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen p-4 text-center sm:p-0">
        <!-- Overlay -->
        <div class="fixed inset-0 bg-gray-900/40 dark:bg-black/60 backdrop-blur-md transition-opacity" @click="closeModal"></div>

        <!-- Modal Content -->
        <div class="relative inline-block bg-white dark:bg-gray-900 rounded-[40px] text-left overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.2)] transform transition-all sm:my-8 sm:max-w-5xl sm:w-full border border-white/20 dark:border-gray-800/50">
          <!-- Modal Header -->
          <div class="px-10 py-8 border-b border-gray-100 dark:border-gray-800/50 relative overflow-hidden">
            <div class="absolute inset-0 bg-gradient-to-r from-primary-500/5 to-blue-500/5 pointer-events-none"></div>
            <div class="flex items-center justify-between relative z-10">
              <div class="flex items-center gap-4">
                <div class="p-4 bg-primary-50 dark:bg-primary-900/20 rounded-2xl shadow-inner">
                  <ScaleIcon class="w-6 h-6 text-primary-500" />
                </div>
                <div>
                  <h3 class="text-2xl font-bold text-gray-900 dark:text-white uppercase tracking-tight">
                    {{ isEditing ? 'Edit Legal Page' : 'Create Legal Page' }}
                  </h3>
                  <p class="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mt-1">Legal Document Management</p>
                </div>
              </div>
              <button @click="closeModal" class="p-3 text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-2xl transition-all">
                <XMarkIcon class="w-6 h-6" />
              </button>
            </div>
          </div>

          <form @submit.prevent="savePage">
            <div class="px-10 py-8 space-y-8 max-h-[70vh] overflow-y-auto custom-scrollbar">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div class="group">
                  <label class="block text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 px-1">Page Title</label>
                  <input
                    v-model="formData.title"
                    type="text"
                    required
                    class="w-full px-5 py-3.5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-[12px] font-bold uppercase tracking-widest text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all shadow-sm"
                    placeholder="e.g. PRIVACY POLICY"
                  />
                </div>

                <div class="group">
                  <label class="block text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 px-1">URL Slug</label>
                  <input
                    v-model="formData.slug"
                    type="text"
                    required
                    pattern="[a-z0-9-]+"
                    class="w-full px-5 py-3.5 bg-gray-100/30 dark:bg-gray-800/20 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-xs font-bold text-gray-900 dark:text-white font-mono tracking-wider focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all shadow-sm"
                    placeholder="privacy-policy"
                  />
                </div>
              </div>

              <div class="group">
                <label class="block text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 px-1">SEO Description</label>
                <textarea
                  v-model="formData.meta_description"
                  rows="2"
                  class="w-full px-5 py-4 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-[28px] text-xs font-bold tracking-wider text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all resize-none shadow-sm"
                  placeholder="Meta description for search results..."
                ></textarea>
              </div>

              <div class="group">
                <div class="flex items-center justify-between mb-3 px-1">
                  <label class="block text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Page Content (HTML)</label>
                  <span class="text-[9px] font-bold text-primary-500 uppercase tracking-widest">HTML Editor Active</span>
                </div>
                <div class="relative group/editor">
                  <textarea
                    v-model="formData.content"
                    rows="15"
                    required
                    class="w-full px-6 py-6 bg-gray-900 dark:bg-gray-800 border border-gray-800 dark:border-gray-700 rounded-[32px] text-[13px] text-emerald-500 font-mono tracking-tight focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all shadow-inner custom-scrollbar leading-relaxed"
                    placeholder="<section class='node'>Initialize protocol data...</section>"
                  ></textarea>
                  <div class="absolute top-4 right-4 flex gap-2">
                    <div class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                    <div class="w-2 h-2 rounded-full bg-blue-500 animate-pulse delay-150"></div>
                  </div>
                </div>
              </div>

              <div class="flex items-center justify-center p-6 bg-gray-50/50 dark:bg-gray-900/10 rounded-[28px] border border-gray-100 dark:border-gray-700/50">
                <label class="relative inline-flex items-center cursor-pointer group">
                  <input type="checkbox" v-model="formData.is_active" class="sr-only peer">
                  <div class="w-14 h-8 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:rounded-full after:h-6 after:w-6 after:shadow-lg after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-primary-500 peer-checked:to-blue-500 shadow-inner"></div>
                  <span class="ml-4 text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest group-hover:text-gray-900 dark:group-hover:text-white transition-colors">Publish Page (Make Public)</span>
                </label>
              </div>
            </div>

            <!-- Modal Footer -->
            <div class="px-10 py-8 bg-gray-50/50 dark:bg-gray-900/50 border-t border-gray-100 dark:border-gray-800/50 flex justify-end gap-4 relative overflow-hidden backdrop-blur-md">
              <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full animate-[shimmer_3s_infinite]"></div>
              <button type="button" @click="closeModal" 
                class="px-8 py-4 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-[11px] font-bold uppercase tracking-widest text-gray-400 hover:text-gray-900 dark:hover:text-white transition-all active:scale-95 shadow-sm">
                Cancel
              </button>
              <button type="submit"
                class="group relative px-10 py-4 bg-gray-900 dark:bg-primary-600 text-white text-[11px] font-bold uppercase tracking-[0.2em] rounded-2xl hover:scale-105 transition-all shadow-xl shadow-primary-500/20 active:scale-95 overflow-hidden">
                <div class="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                <span class="flex items-center gap-2">
                  <ArrowPathIcon v-if="loading" class="animate-spin h-4 w-4" />
                  {{ isEditing ? 'Save Changes' : 'Create Page' }}
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal (Premium) -->
    <div v-if="showDeleteModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen p-4 text-center">
        <div class="fixed inset-0 bg-gray-900/40 dark:bg-black/80 backdrop-blur-sm" @click="showDeleteModal = false"></div>

        <div class="relative inline-block w-full max-w-md p-10 overflow-hidden text-center align-middle transition-all transform bg-white dark:bg-gray-900 shadow-2xl rounded-[40px] border border-gray-100 dark:border-gray-800">
          <div class="flex items-center justify-center w-20 h-20 mx-auto bg-rose-50 dark:bg-rose-900/20 rounded-3xl mb-6">
            <TrashIcon class="w-10 h-10 text-rose-600" />
          </div>
          <h3 class="text-2xl font-bold text-gray-900 dark:text-white uppercase tracking-tight mb-2">Delete Legal Page</h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 font-medium">
            Are you sure you want to delete <br/>
            <span class="text-rose-500 font-bold">"{{ pageToDelete?.title }}"</span>? <br/>
            This action cannot be undone.
          </p>
          <div class="flex gap-4 mt-10">
            <button @click="showDeleteModal = false"
              class="flex-1 px-4 py-4 bg-gray-50 dark:bg-gray-800 text-[11px] font-bold text-gray-400 uppercase tracking-widest rounded-2xl hover:text-gray-900 dark:hover:text-white transition-all">
              Cancel
            </button>
            <button @click="deletePage"
              class="flex-1 px-4 py-4 bg-rose-600 text-white text-[11px] font-bold uppercase tracking-widest rounded-2xl hover:bg-rose-700 shadow-xl shadow-rose-500/20 transition-all active:scale-95">
              Delete Page
            </button>
          </div>
        </div>
      </div>
    </div>
  </AppSidebar>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { 
  PlusIcon, DocumentTextIcon, PencilSquareIcon, 
  TrashIcon, ScaleIcon, XMarkIcon, ArrowPathIcon 
} from '@heroicons/vue/24/outline'
import { useAuthStore } from '@/stores/auth'
import { useUIStore } from '@/stores/ui'
import AppSidebar from '@/components/layout/AppSidebar.vue'

const authStore = useAuthStore()
const uiStore = useUIStore()

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1'

const loading = ref(true)
const legalPages = ref([])
const showModal = ref(false)
const showDeleteModal = ref(false)
const isEditing = ref(false)
const pageToDelete = ref(null)

const formData = ref({
  title: '',
  slug: '',
  content: '',
  meta_description: '',
  is_active: true
})

const getHeaders = () => ({
  'Content-Type': 'application/json',
  'Accept': 'application/json',
  'Authorization': `Bearer ${authStore.token}`
})

const fetchLegalPages = async () => {
  loading.value = true
  try {
    const response = await fetch(`${API_BASE_URL}/admin/legal-pages`, {
      headers: getHeaders()
    })
    const data = await response.json()
    if (data.success) {
      legalPages.value = data.data
    }
  } catch (error) {
    console.error('Error fetching legal pages:', error)
    uiStore.showNotification({
      type: 'error',
      title: 'Error',
      message: 'Failed to load legal pages'
    })
  } finally {
    loading.value = false
  }
}

const openCreateModal = () => {
  isEditing.value = false
  formData.value = {
    title: '',
    slug: '',
    content: '',
    meta_description: '',
    is_active: true
  }
  showModal.value = true
}

const openEditModal = async (page) => {
  isEditing.value = true
  try {
    const response = await fetch(`${API_BASE_URL}/admin/legal-pages/${page.id}`, {
      headers: getHeaders()
    })
    const data = await response.json()
    if (data.success) {
      formData.value = { ...data.data }
      showModal.value = true
    }
  } catch (error) {
    console.error('Error loading page:', error)
    uiStore.showNotification({
      type: 'error',
      title: 'Error',
      message: 'Failed to load page details'
    })
  }
}

const closeModal = () => {
  showModal.value = false
  formData.value = {
    title: '',
    slug: '',
    content: '',
    meta_description: '',
    is_active: true
  }
}

const savePage = async () => {
  try {
    const url = isEditing.value
      ? `${API_BASE_URL}/admin/legal-pages/${formData.value.id}`
      : `${API_BASE_URL}/admin/legal-pages`
    
    const method = isEditing.value ? 'PUT' : 'POST'

    const response = await fetch(url, {
      method,
      headers: getHeaders(),
      body: JSON.stringify(formData.value)
    })

    const data = await response.json()

    if (data.success) {
      uiStore.showNotification({
        type: 'success',
        title: 'Success',
        message: data.message
      })
      closeModal()
      await fetchLegalPages()
    } else {
      throw new Error(data.message || 'Failed to save page')
    }
  } catch (error) {
    console.error('Error saving page:', error)
    uiStore.showNotification({
      type: 'error',
      title: 'Error',
      message: error.message || 'Failed to save page'
    })
  }
}

const toggleStatus = async (page) => {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/legal-pages/${page.id}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify({
        ...page,
        is_active: !page.is_active
      })
    })

    const data = await response.json()

    if (data.success) {
      uiStore.showNotification({
        type: 'success',
        title: 'Success',
        message: `Page ${data.data.is_active ? 'activated' : 'deactivated'} successfully`
      })
      await fetchLegalPages()
    }
  } catch (error) {
    console.error('Error toggling status:', error)
    uiStore.showNotification({
      type: 'error',
      title: 'Error',
      message: 'Failed to update page status'
    })
  }
}

const confirmDelete = (page) => {
  pageToDelete.value = page
  showDeleteModal.value = true
}

const deletePage = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/legal-pages/${pageToDelete.value.id}`, {
      method: 'DELETE',
      headers: getHeaders()
    })

    const data = await response.json()

    if (data.success) {
      uiStore.showNotification({
        type: 'success',
        title: 'Success',
        message: data.message
      })
      showDeleteModal.value = false
      pageToDelete.value = null
      await fetchLegalPages()
    }
  } catch (error) {
    console.error('Error deleting page:', error)
    uiStore.showNotification({
      type: 'error',
      title: 'Error',
      message: 'Failed to delete page'
    })
  }
}

const formatDate = (dateString) => {
  if (!dateString) return 'N/A'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

onMounted(() => {
  fetchLegalPages()
})
</script>
